import { useState, type FormEvent } from 'react';
import { ApiError, type Sex } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { useTheme } from '../lib/theme.js';

type Mode = 'login' | 'signup';

/**
 * L'inscription est en deux temps : identifiants, puis morphologie. Elle sert
 * au seul modèle de dépense de repli, aussi la seconde étape est-elle
 * entièrement facultative — et elle le dit.
 */
const STEPS = ['Identifiants', 'Morphologie'] as const;

const PITCH: Record<Mode, { headline: string[]; lead: string }> = {
  login: {
    headline: ['Trois chiffres par jour.', 'Une courbe qui parle.'],
    lead: "Poids du matin, pas et calories de la veille. Ephemeris s'occupe des moyennes, des tendances et des corrélations.",
  },
  signup: {
    headline: ['Deux semaines de données', 'et la tendance devient lisible.'],
    lead: 'Le poids de départ sert de repère initial, la morphologie à estimer une dépense tant que les saisies ne suffisent pas. Tout le reste se remplit au fil des jours, trois champs à la fois.',
  },
};

const SEXES: Sex[] = ['female', 'male', 'other'];
const SEX_LABELS: Record<Sex, string> = { female: 'Femme', male: 'Homme', other: 'Autre' };

/** La virgule décimale est l'écriture naturelle en français, et le clavier
 *  numérique Android la propose avant le point. */
function toNumber(text: string): number | null {
  const value = Number.parseFloat(text.replace(',', '.'));
  return Number.isFinite(value) ? value : null;
}

export function AuthScreen() {
  const { login, register, allowRegistration } = useAuth();
  const [theme, setTheme] = useTheme();
  const [mode, setMode] = useState<Mode>('login');
  const [step, setStep] = useState(0);

  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [startWeight, setStartWeight] = useState('');
  const [sex, setSex] = useState<Sex | null>(null);
  const [birthYear, setBirthYear] = useState('');
  const [heightCm, setHeightCm] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * Confirmation affichee *sur* le formulaire de connexion. Une demande
   * deposee ramene donc la personne la ou elle reviendra, adresse deja
   * remplie, plutot que sur un ecran separe dont il faut ressortir a la main.
   */
  const [notice, setNotice] = useState<string | null>(null);

  const pitch = PITCH[mode];
  const thisYear = new Date().getFullYear();

  async function createAccount() {
    return register({
      email,
      password,
      displayName: firstName.trim() || undefined,
      startWeight: toNumber(startWeight),
      sex,
      birthYear: toNumber(birthYear),
      heightCm: toNumber(heightCm),
    });
  }

  async function submit(event: FormEvent) {
    event.preventDefault();

    // Première étape : la validation HTML des champs requis vient de passer,
    // on avance sans rien envoyer.
    if (mode === 'signup' && step === 0) {
      setStep(1);
      setError(null);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      if (mode === 'login') {
        await login(email, password);
      } else if (await createAccount()) {
        setMode('login');
        setStep(0);
        // Le mot de passe est efface : il ne servira qu'apres validation, et le
        // laisser en place suggererait qu'une connexion est possible tout de suite.
        setPassword('');
        setNotice(
          'Demande enregistrée. Votre accès s’ouvrira une fois validé — revenez vous connecter avec cette adresse.',
        );
      }
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Connexion impossible');
      // Une adresse déjà prise se corrige à la première étape.
      if (cause instanceof ApiError && cause.code === 'email_taken') setStep(0);
    } finally {
      setBusy(false);
    }
  }

  function switchMode() {
    setMode(mode === 'login' ? 'signup' : 'login');
    setStep(0);
    setError(null);
    setNotice(null);
  }

  const submitLabel = busy
    ? 'Un instant…'
    : mode === 'login'
      ? 'Se connecter'
      : step === 0
        ? 'Continuer'
        : 'Créer mon compte';

  return (
    <main className="auth">
      <section className="auth__pitch">
        <div className="brand">
          <span>Ephemeris</span>
        </div>
        <div>
          <h1 className="auth__headline">
            {pitch.headline[0]}
            <br />
            {pitch.headline[1]}
          </h1>
          <p className="auth__lead">{pitch.lead}</p>
        </div>
        <div className="auth__metrics">
          <span>Poids</span>
          <span>Pas</span>
          <span>Kcal</span>
        </div>
      </section>

      <section className="auth__form">
        <form onSubmit={submit}>
          <div className="eyebrow" style={{ marginBottom: 22 }}>
            {mode === 'login' ? 'Connexion' : `Créer un compte · ${STEPS[step]}`}
          </div>

          {mode === 'signup' ? (
            <div className="auth__steps" aria-hidden="true">
              {STEPS.map((label, index) => (
                <span key={label} className={index <= step ? 'is-done' : ''} />
              ))}
            </div>
          ) : null}

          {notice ? (
            <p className="auth__notice" role="status">
              {notice}
            </p>
          ) : null}

          <div className="auth__fields">
            {mode === 'signup' && step === 0 ? (
              <label className="field">
                <span>Prénom</span>
                <input
                  value={firstName}
                  onChange={(event) => setFirstName(event.target.value)}
                  autoComplete="given-name"
                  maxLength={80}
                />
              </label>
            ) : null}

            {mode === 'login' || step === 0 ? (
              <>
                <label className="field">
                  <span>Adresse e-mail</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    autoComplete="username"
                    inputMode="email"
                    required
                  />
                </label>

                <label className="field">
                  <span>Mot de passe</span>
                  <input
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                    minLength={8}
                    required
                  />
                  {mode === 'signup' ? <span className="hint">8 caractères minimum</span> : null}
                </label>
              </>
            ) : null}

            {mode === 'signup' && step === 1 ? (
              <>
                <label className="field">
                  <span>Poids de départ</span>
                  <div className="inputgroup">
                    <input
                      value={startWeight}
                      onChange={(event) => setStartWeight(event.target.value)}
                      inputMode="decimal"
                      placeholder="85,4"
                    />
                    <span>kg</span>
                  </div>
                  <span className="hint">Simple repère, jamais recalculé.</span>
                </label>

                <div className="field">
                  <span>Sexe</span>
                  <div className="segmented" role="group" aria-label="Sexe">
                    {SEXES.map((option) => (
                      <button
                        key={option}
                        type="button"
                        aria-pressed={sex === option}
                        onClick={() => setSex(sex === option ? null : option)}
                      >
                        {SEX_LABELS[option]}
                      </button>
                    ))}
                  </div>
                </div>

                <label className="field">
                  <span>Année de naissance</span>
                  <input
                    type="number"
                    value={birthYear}
                    onChange={(event) => setBirthYear(event.target.value)}
                    min={1900}
                    max={thisYear}
                    inputMode="numeric"
                    placeholder="1990"
                    autoComplete="bday-year"
                  />
                </label>

                <label className="field">
                  <span>Taille</span>
                  <div className="inputgroup">
                    <input
                      type="number"
                      value={heightCm}
                      onChange={(event) => setHeightCm(event.target.value)}
                      min={81}
                      max={259}
                      step={0.5}
                      inputMode="decimal"
                      placeholder="178"
                    />
                    <span>cm</span>
                  </div>
                  <span className="hint">
                    Taille et année de naissance ne servent qu’à estimer une dépense de repos, le
                    temps que quatorze jours de saisies prennent le relais. On peut les laisser
                    vides et les renseigner plus tard.
                  </span>
                </label>
              </>
            ) : null}

            {error ? (
              <p className="error" role="alert">
                {error}
              </p>
            ) : null}

            <button type="submit" className="btn" disabled={busy}>
              {submitLabel}
            </button>

            {mode === 'signup' && step === 1 ? (
              <button
                type="button"
                className="linkbtn"
                disabled={busy}
                onClick={() => setStep(0)}
              >
                Revenir aux identifiants
              </button>
            ) : null}
          </div>

          <div className="auth__actions">
            {mode === 'login' ? (
              allowRegistration ? (
                <button type="button" className="linkbtn" onClick={switchMode}>
                  Créer un compte
                </button>
              ) : (
                <span>Inscriptions fermées</span>
              )
            ) : (
              <button type="button" className="linkbtn" onClick={switchMode}>
                J’ai déjà un compte
              </button>
            )}
            <button
              type="button"
              className="linkbtn"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            >
              {theme === 'dark' ? 'Thème clair' : 'Thème sombre'}
            </button>
          </div>
        </form>
      </section>
    </main>
  );
}
